import React, { useMemo } from 'react'
import {
  PiHouseFill,
  PiHouse,
  PiMegaphoneFill,
  PiMegaphone,
  PiHandCoinsFill,
  PiHandCoins,
  PiSealPercentFill,
  PiSealPercent,
  PiHandshakeFill,
  PiHandshake,
  PiChartLineUpFill,
  PiChartLineUp,
  PiGearFill,
  PiGear,
} from 'react-icons/pi'
import {
  TargetArrowIcon,
  TargetArrowOutlineIcon,
  WorkspaceFilledIcon,
  WorkspaceOutlineIcon,
} from './nav-icons'
import apiService from 'services/api'
import { useAuth } from 'context/authentication/useAuth'
import type { NavItemConfig, NavChildItem, NavSection } from './types'
import type { FeatureFlags } from 'utils/featureFlagService'
import type { User } from 'models/user'

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export function resolvePath(path: string | (() => string)): string {
  return typeof path === 'function' ? path() : path
}

export function isNavItemEnabled(
  item: Pick<
    NavChildItem,
    | 'enabledBy'
    | 'requiredCompanyProperty'
    | 'requiredUserProperty'
    | 'customValidation'
  >,
  featureFlags: FeatureFlags,
  user?: User
): boolean {
  if (item.enabledBy && !item.enabledBy.every((key) => featureFlags[key])) {
    return false
  }

  if (item.requiredCompanyProperty) {
    if (
      !user?.company ||
      !item.requiredCompanyProperty.every((key) => user.company[key] === true)
    ) {
      return false
    }
  }

  if (item.requiredUserProperty && user) {
    if (!item.requiredUserProperty.every((key) => user[key] === true)) {
      return false
    }
  }

  if (item.customValidation) {
    if (!user || !item.customValidation(featureFlags, user)) {
      return false
    }
  }

  return true
}

export function hasSlideOut(item: NavItemConfig): boolean {
  return !!item.children
}

export function resolveChildren(
  item: NavItemConfig
): NavChildItem[] | undefined {
  if (typeof item.children === 'function') return item.children()
  return item.children
}

export function getNavItem(
  items: NavItemConfig[],
  id: NavSection
): NavItemConfig | undefined {
  return items.find((item) => item.id === id)
}

/**
 * Determine the active NavSection from a pathname.
 *
 * Checks children paths first (e.g. /documents → workspace),
 * then falls back to top-level item paths (e.g. /deals → deals).
 */
export function getActiveNavSection(
  pathname: string,
  items: NavItemConfig[]
): NavSection | null {
  for (const item of items) {
    const children = resolveChildren(item)
    if (children) {
      for (const child of children) {
        if (pathname.startsWith(resolvePath(child.path))) {
          return item.id
        }
      }
    }
  }

  for (const item of items) {
    if (pathname.startsWith(resolvePath(item.path))) {
      return item.id
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBaseNavItems() {
  const api = apiService()
  const { isAuthenticated, isLimited } = useAuth()
  const { data: companyCatAndSub, isLoading: targetsLoading } =
    api.useGetActiveCompanyCategoriesAndTypes(!isAuthenticated || isLimited)

  const targetsChildren = useMemo((): NavChildItem[] | undefined => {
    if (!companyCatAndSub) return undefined
    return companyCatAndSub.map((cat) => ({
      id: cat.slug,
      label: cat.name,
      description: cat.description || undefined,
      path: `/targets/${cat.slug}/${cat.sub_categories?.[0]?.slug || ''}`,
      showChildrenBreadcrumb: true,
      children: cat.sub_categories.map((sub) => ({
        id: sub.slug,
        label: sub.name,
        path: `/targets/${cat.slug}/${sub.slug}`,
      })),
    }))
  }, [companyCatAndSub])

  const navItems: NavItemConfig[] = useMemo(() => {
    const items: NavItemConfig[] = [
      {
        id: 'home',
        label: 'Home',
        path: '/home',
        icon: <PiHouseFill size={24} />,
        outlineIcon: <PiHouse size={24} />,
        enabledBy: ['enableHomepage'],
      },
      {
        id: 'targets',
        label: 'Targets',
        path: '/targets',
        icon: <TargetArrowIcon size={24} />,
        outlineIcon: <TargetArrowOutlineIcon size={24} />,
        children: () => targetsChildren,
        isLoading: targetsLoading,
      },
      {
        id: 'campaigns',
        label: 'Campaigns',
        path: '/campaigns',
        icon: <PiMegaphoneFill size={24} />,
        outlineIcon: <PiMegaphone size={24} />,
        enabledBy: ['enableCampaigns'],
        children: [
          { id: 'all-campaigns', label: 'All Campaigns', path: '/campaigns' },
        ],
      },
      {
        id: 'samples',
        label: 'Samples',
        path: '/samples',
        minimumRole: 'Limited',
        icon: <PiHandCoinsFill size={24} />,
        outlineIcon: <PiHandCoins size={24} />,
        enabledBy: ['enableSamples'],
      },
      {
        id: 'rebates',
        label: 'Rebates',
        path: '/rebates',
        icon: <PiSealPercentFill size={24} />,
        outlineIcon: <PiSealPercent size={24} />,
      },
      {
        id: 'deals',
        label: 'Deals',
        path: '/deals',
        icon: <PiHandshakeFill size={24} />,
        outlineIcon: <PiHandshake size={24} />,
      },
      {
        id: 'performance',
        label: 'Performance',
        path: '/dashboard',
        icon: <PiChartLineUpFill size={24} />,
        outlineIcon: <PiChartLineUp size={24} />,
        children: [{ id: 'dashboard', label: 'Dashboard', path: '/dashboard' }],
      },
      {
        id: 'workspace',
        label: 'Workspace',
        path: () => (isLimited ? '/products' : '/documents'),
        minimumRole: 'Limited',
        icon: <WorkspaceFilledIcon size={24} />,
        outlineIcon: <WorkspaceOutlineIcon size={24} />,
        children: [
          {
            id: 'documents',
            label: 'Documents',
            path: '/documents',
            enabledBy: ['enableDocumentLibrary'],
          },
          { id: 'contacts', label: 'Contacts', path: '/contacts' },
          { id: 'activity', label: 'Activity', path: '/activity' },
          {
            id: 'products',
            label: 'Products',
            minimumRole: 'Limited',
            path: '/products',
          },
          {
            id: 'brands',
            label: 'Brands',
            path: '/brands',
            enabledBy: ['enableBrandManagement'],
          },
          {
            id: 'reminders',
            label: 'Reminders',
            path: '/reminders',
            enabledBy: ['enableReminders'],
          },
        ],
      },
      {
        id: 'settings',
        label: 'Settings',
        path: '/settings',
        icon: <PiGearFill size={24} />,
        outlineIcon: <PiGear size={24} />,
        children: [
          {
            id: 'company',
            label: 'Company',
            path: '/settings/company',
            showChildrenBreadcrumb: true,
            customValidation: (featureFlags, user) => {
              if (featureFlags.enableUserTypes) {
                if (!user.user_type || user.user_type === 'Limited') {
                  return false
                }

                return true
              }

              if (user.is_billing_admin) {
                return true
              }

              return false
            },
            children: [
              {
                id: 'account',
                label: 'Account',
                path: '/settings/company/account',
              },
              {
                id: 'logo',
                label: 'Logo',
                path: '/settings/company/logo',
              },
              {
                id: 'domains',
                label: 'Domains',
                path: '/settings/company/domains',
                enabledBy: ['enableCompanyEmailExtensionSettings'],
              },
            ],
          },
          {
            id: 'users',
            label: 'Users',
            path: '/settings/users',
            showChildrenBreadcrumb: true,
            children: [
              {
                id: 'users-tab',
                label: 'Users',
                path: '/settings/users/users',
              },
              {
                id: 'email-integration',
                label: 'Email Integration',
                path: '/settings/users/email-integration',
                enabledBy: ['enableEmailIntegration'],
              },
            ],
          },
          {
            id: 'platform',
            label: 'Platform',
            path: '/settings/platform',
            showChildrenBreadcrumb: true,
            children: [
              {
                id: 'sales-stages',
                label: 'Sales Stages',
                path: '/settings/platform/sales-stages',
              },
              {
                id: 'distributors',
                label: 'Distributors',
                path: '/settings/platform/distributors',
              },
              {
                id: 'gpos',
                label: 'GPOs',
                path: '/settings/platform/gpos',
              },
            ],
          },
          {
            id: 'data',
            label: 'Data',
            path: '/settings/data',
            showChildrenBreadcrumb: true,
            children: [
              {
                id: 'filters',
                label: 'Filters',
                path: '/settings/data/filters',
              },
              {
                id: 'product-tags',
                label: 'Tags',
                path: '/settings/data/product-tags',
              },
              {
                id: 'contact-tags',
                label: 'Contact Labels',
                path: '/settings/data/contact-tags',
              },
              {
                id: 'document-tags',
                label: 'Document Tags',
                path: '/settings/data/document-tags',
              },
              {
                id: 'deal-tags',
                label: 'Deal Tags',
                path: '/settings/data/deal-tags',
              },
              {
                id: 'columns',
                label: 'Column Presets',
                path: '/settings/data/columns',
              },
            ],
          },
          {
            id: 'notifications',
            label: 'Notifications',
            path: '/settings/notifications',
            enabledBy: ['enableUserNotificationPreferences'],
            showChildrenBreadcrumb: true,
            children: [
              {
                id: 'notifications-platform',
                label: 'Platform',
                path: '/settings/notifications/platform',
              },
              {
                id: 'notifications-deals',
                label: 'Deals',
                path: '/settings/notifications/deals',
              },
              {
                id: 'notifications-contacts',
                label: 'Contacts',
                path: '/settings/notifications/contacts',
              },
              {
                id: 'notifications-campaigns',
                label: 'Campaigns',
                path: '/settings/notifications/campaigns',
              },
              {
                id: 'notifications-rebates',
                label: 'Rebates',
                path: '/settings/notifications/rebates',
              },
            ],
          },
          {
            id: 'misc',
            label: 'Misc Settings',
            path: '/settings/misc',
          },
          {
            id: 'automations',
            label: 'Automations',
            path: '/settings/automations',
            enabledBy: ['automationsPageEnabled'],
          },
          {
            id: 'email-outreach',
            label: 'Email Outreach',
            path: '/settings/email-outreach',
            enabledBy: ['enableEmailSequencing'],
            requiredCompanyProperty: ['has_bison_workspace'],
          },
        ],
      },
    ]

    return items
  }, [targetsChildren, targetsLoading])

  return { navItems, isLoading: targetsLoading }
}
